// routes
import { PATH_DASHBOARD } from '../../../routes/paths';
// staff roles
import { rolesForRoute } from '../../../constants/staffRoles';
// components
import SvgIconStyle from '../../../components/SvgIconStyle';
import UploadRequestsBadge from './UploadRequestsBadge';

// ----------------------------------------------------------------------

const getIcon = (name: string) => (
  <SvgIconStyle src={`/assets/icons/navbar/${name}.svg`} sx={{ width: 1, height: 1 }} />
);

const ICONS = {
  dashboard: getIcon('ic_dashboard'),
  user: getIcon('ic_user'),
  banking: getIcon('ic_banking'),
  invoice: getIcon('ic_invoice'),
  calendar: getIcon('ic_calendar'),
  kanban: getIcon('ic_kanban'),
  cart: getIcon('ic_cart'),
  booking: getIcon('ic_booking'),
  analytics: getIcon('ic_analytics'),
  mail: getIcon('ic_mail'),
  chat: getIcon('ic_chat'),
  ecommerce: getIcon('ic_ecommerce'),
};

// A menu item shows when one of the user's roles may read the page's main list
// (Appendix A). An item with no roles shows to everyone.
const can = (path: string) => rolesForRoute('GET', path);

const navConfig = [
  // ----------------------------------------------------------------------
  // HOME
  // ----------------------------------------------------------------------
  {
    subheader: 'STAFF PANEL',
    items: [
      {
        title: 'Home',
        path: PATH_DASHBOARD.overview,
        icon: ICONS.dashboard,
      },
    ],
  },

  // ----------------------------------------------------------------------
  // CLIENTS & AGENTS
  // ----------------------------------------------------------------------
  {
    subheader: 'CLIENT MANAGEMENT',
    items: [
      {
        title: 'All Clients',
        path: PATH_DASHBOARD.clients.all,
        icon: ICONS.user,
        roles: can('/owner/clients'),
      },
      {
        title: 'Field Agents',
        path: PATH_DASHBOARD.agents.root,
        icon: ICONS.booking,
        roles: can('/owner/agents'),
      },
    ],
  },

  // ----------------------------------------------------------------------
  // APP USERS: upload requests and updates (announcements)
  // ----------------------------------------------------------------------
  {
    subheader: 'APP USERS',
    items: [
      {
        title: 'Upload Requests',
        path: PATH_DASHBOARD.uploadRequests.root,
        icon: ICONS.invoice,
        info: <UploadRequestsBadge />,
        roles: can('/owner/upload-requests'),
      },
      {
        title: 'Updates',
        path: PATH_DASHBOARD.announcements.root,
        icon: ICONS.chat,
        roles: can('/owner/announcements'),
      },
    ],
  },

  // ----------------------------------------------------------------------
  // WEBSITE LEADS
  // ----------------------------------------------------------------------
  {
    subheader: 'WEBSITE LEADS',
    items: [
      {
        title: 'Demo / Quote Queries',
        path: PATH_DASHBOARD.websiteLeads.queries,
        icon: ICONS.mail,
        roles: can('/owner/queries'),
      },
      {
        title: 'Partner Requests',
        path: PATH_DASHBOARD.websiteLeads.partnerRequests,
        icon: ICONS.chat,
        roles: can('/owner/partner-requests'),
      },
    ],
  },

  // ----------------------------------------------------------------------
  // ELECTION ASSETS
  // ----------------------------------------------------------------------
  {
    subheader: 'ELECTION ASSETS',
    items: [
      {
        title: 'Voter Signs',
        path: PATH_DASHBOARD.voterSigns.root,
        icon: ICONS.ecommerce,
        roles: can('/owner/voter-signs'),
      },
      {
        title: 'Polling Schedules',
        path: PATH_DASHBOARD.pollingSchedules.root,
        icon: ICONS.calendar,
        roles: can('/owner/polling-schedules'),
      },
      {
        title: 'Locations',
        path: PATH_DASHBOARD.locations.root,
        icon: ICONS.kanban,
        roles: can('/owner/locations/districts'),
      },
      {
        title: 'Social Platforms',
        path: PATH_DASHBOARD.settings.socialPlatforms,
        icon: ICONS.chat,
        roles: can('/owner/social-platforms'),
      },
    ],
  },

  // ----------------------------------------------------------------------
  // PLANS & SUBSCRIPTIONS
  // ----------------------------------------------------------------------
  {
    subheader: 'PLANS & SUBSCRIPTIONS',
    items: [
      {
        title: 'Plans',
        path: PATH_DASHBOARD.plansAndSubscriptions.plans,
        icon: ICONS.kanban,
        roles: can('/owner/plans'),
      },
      {
        title: 'Subscriptions',
        path: PATH_DASHBOARD.plansAndSubscriptions.subscriptions,
        icon: ICONS.booking,
        roles: can('/owner/subscriptions'),
      },
      {
        title: 'Free Trial Limits',
        path: PATH_DASHBOARD.plansAndSubscriptions.trialSettings,
        icon: ICONS.analytics,
        roles: can('/owner/trial-settings'),
      },
    ],
  },

  // ----------------------------------------------------------------------
  // PROMO CODES
  // ----------------------------------------------------------------------
  {
    subheader: 'PROMO CODES',
    items: [
      {
        title: 'Promo Codes',
        path: PATH_DASHBOARD.promoCodes.root,
        icon: ICONS.cart,
        roles: can('/owner/promocodes'),
      },
      {
        title: 'Partner Code Percent',
        path: PATH_DASHBOARD.promoCodes.partnerCodeSettings,
        icon: ICONS.cart,
        roles: can('/owner/partner-code-settings'),
      },
    ],
  },

  // ----------------------------------------------------------------------
  // PAYMENTS & INVOICES
  // ----------------------------------------------------------------------
  {
    subheader: 'PAYMENTS & BILLING',
    items: [
      {
        title: 'Payments',
        path: PATH_DASHBOARD.paymentsAndInvoices.root,
        icon: ICONS.banking,
        roles: can('/owner/payments'),
      },
      {
        title: 'Invoices',
        path: PATH_DASHBOARD.paymentsAndInvoices.invoices,
        icon: ICONS.invoice,
        roles: can('/owner/invoices'),
      },
    ],
  },

  // ----------------------------------------------------------------------
  // PARTNERS
  // ----------------------------------------------------------------------
  {
    subheader: 'PARTNER PROGRAM',
    items: [
      {
        title: 'Partners',
        path: PATH_DASHBOARD.partners.all,
        icon: ICONS.user,
        roles: can('/owner/partners'),
      },
      {
        title: 'Partner Orders',
        path: PATH_DASHBOARD.partners.orders,
        icon: ICONS.cart,
        roles: can('/owner/partner-orders'),
      },
      {
        title: 'Location Requests',
        path: PATH_DASHBOARD.partners.locationRequests,
        icon: ICONS.booking,
        roles: can('/owner/partner-location-requests'),
      },
      {
        title: 'Commissions',
        path: PATH_DASHBOARD.commissions.rules,
        icon: ICONS.banking,
        children: [
          {
            title: 'Rules',
            path: PATH_DASHBOARD.commissions.rules,
            roles: can('/owner/commission-rules'),
          },
          {
            title: 'Ledger',
            path: PATH_DASHBOARD.commissions.ledger,
            roles: can('/owner/commissions'),
          },
          {
            title: 'Payouts',
            path: PATH_DASHBOARD.commissions.payouts,
            roles: can('/owner/payouts'),
          },
          {
            title: 'Unconfigured Orders',
            path: PATH_DASHBOARD.commissions.unconfigured,
            roles: can('/owner/commissions/unconfigured'),
          },
          {
            title: 'Reports & Exports',
            path: PATH_DASHBOARD.commissions.reports,
            roles: can('/owner/partner-reports/summary'),
          },
          {
            title: 'Settings',
            path: PATH_DASHBOARD.commissions.settings,
            roles: can('/owner/commission-settings'),
          },
        ],
      },
      {
        title: 'Partner KYC',
        path: PATH_DASHBOARD.kyc.queue,
        icon: ICONS.invoice,
        children: [
          { title: 'Review Queue', path: PATH_DASHBOARD.kyc.queue, roles: can('/owner/kyc/queue') },
          {
            title: 'Settings',
            path: PATH_DASHBOARD.kyc.settings,
            roles: can('/owner/kyc-settings'),
          },
        ],
      },
      {
        title: 'Material Wallets',
        path: PATH_DASHBOARD.wallets.overview,
        icon: ICONS.banking,
        children: [
          {
            title: 'Overview & Summary',
            path: PATH_DASHBOARD.wallets.overview,
            roles: can('/owner/wallet/summary'),
          },
          { title: 'All Wallets', path: PATH_DASHBOARD.wallets.all, roles: can('/owner/wallets') },
          {
            title: 'Recharges',
            path: PATH_DASHBOARD.wallets.recharges,
            roles: can('/owner/wallet/recharges'),
          },
          {
            title: 'Reconciliation',
            path: PATH_DASHBOARD.wallets.reconcile,
            roles: can('/owner/wallet/reconciliation'),
          },
          {
            title: 'Wallet Settings',
            path: PATH_DASHBOARD.wallets.settings,
            roles: can('/owner/wallet/settings'),
          },
        ],
      },
      {
        title: 'Material Orders',
        path: PATH_DASHBOARD.material.orders,
        icon: ICONS.cart,
        children: [
          {
            title: 'Orders List',
            path: PATH_DASHBOARD.material.orders,
            roles: can('/owner/material/orders'),
          },
          {
            title: 'Reports & Exports',
            path: PATH_DASHBOARD.material.reports,
            // The page shows only the reports the roles allow; usage is open to order_ops too
            roles: can('/owner/material/reports/usage'),
          },
          {
            title: 'Stuck Jobs Queue',
            path: PATH_DASHBOARD.material.stuck,
            roles: can('/owner/material/jobs/stuck'),
          },
          {
            title: 'Postcards Monitor',
            path: PATH_DASHBOARD.material.postcards,
            roles: can('/owner/material/postcards'),
          },
          {
            title: 'Voter Data',
            path: PATH_DASHBOARD.material.slipData,
            roles: can('/owner/material/slip-data'),
          },
          {
            title: 'Products & Rates',
            path: PATH_DASHBOARD.material.products,
            roles: can('/owner/material/products'),
          },
          {
            title: 'Material Settings',
            path: PATH_DASHBOARD.material.settings,
            roles: can('/owner/material/settings'),
          },
        ],
      },
    ],
  },
];

export default navConfig;
